// Седемте пресета на избора на период в склада. Смятат се спрямо ДНЕС в
// момента на отваряне (не при зареждане на модула) — страница, отворена
// вечерта, утре сутринта да казва „Днес" за новия ден.
import dayjs, { type Dayjs } from "dayjs";

export type PeriodPresetKey =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "thisYear";

// `preset` — от кой пресет е избран периодът. Пресетите се застъпват по дни
// (на 30-и „Този месец" = „Последните 30 дни", на 1-во = „Днес"), затова
// етикетът и фокусът вървят по избора, не по първото съвпадение.
export type Period = { from: Dayjs; to: Dayjs; preset?: PeriodPresetKey };

export const PERIOD_PRESET_KEYS: PeriodPresetKey[] = [
  "today",
  "yesterday",
  "last7",
  "last30",
  "thisMonth",
  "lastMonth",
  "thisYear",
];

export const periodOfPreset = (key: PeriodPresetKey, now = dayjs()): Period => ({
  ...presetRange(key, now),
  preset: key,
});

const presetRange = (key: PeriodPresetKey, now: Dayjs): Period => {
  const today = now.startOf("day");
  switch (key) {
    case "today":
      return { from: today, to: today };
    case "yesterday":
      return { from: today.subtract(1, "day"), to: today.subtract(1, "day") };
    case "last7":
      return { from: today.subtract(6, "day"), to: today };
    case "last30":
      return { from: today.subtract(29, "day"), to: today };
    case "thisMonth":
      return { from: today.startOf("month"), to: today };
    case "lastMonth": {
      const previous = today.subtract(1, "month");
      return { from: previous.startOf("month"), to: previous.endOf("month").startOf("day") };
    }
    case "thisYear":
      return { from: today.startOf("year"), to: today };
  }
};

const matchesPreset = (key: PeriodPresetKey, period: Period) => {
  const preset = presetRange(key, dayjs());
  return preset.from.isSame(period.from, "day") && preset.to.isSame(period.to, "day");
};

// Кой пресет е периодът — за етикета на бутона и фокуса в панела. Избраният
// пресет печели, докато още описва същите дни (след полунощ „Днес" от вчера
// вече не е днес); иначе — първият, който съвпада по дни.
export const presetOfPeriod = (period: Period): PeriodPresetKey | null => {
  if (period.preset && matchesPreset(period.preset, period)) return period.preset;
  return PERIOD_PRESET_KEYS.find((key) => matchesPreset(key, period)) ?? null;
};

// ISO граници за заявка към BE: от началото на първия до края на последния ден
export const periodToParams = (period: Period) => ({
  startDate: period.from.startOf("day").toISOString(),
  endDate: period.to.endOf("day").toISOString(),
});

export const formatPeriod = (period: Period) =>
  period.from.isSame(period.to, "day")
    ? period.from.format("DD.MM.YYYY")
    : `${period.from.format("DD.MM.YYYY")} – ${period.to.format("DD.MM.YYYY")}`;
